import { useState } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import { Plus, Edit, Trash2, Loader2, AlertCircle, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { CategoryFormModal } from './CategoryFormModal';
import {
  createCategory,
  updateCategory,
  deleteCategory,
} from '../../../api/condition-suggestions.api';
import type { ConditionSuggestionCategory } from '../../../api/condition-suggestions.api';

interface CategoriesTabProps {
  /** Current list of categories (managed by the parent page). */
  categories: ConditionSuggestionCategory[];
  /** True while the parent is fetching categories. */
  isLoading: boolean;
  /** Non-null when the last fetch failed. */
  error: Error | null;
  /** Called after any successful mutation so the parent can refresh. */
  onRefresh: () => void;
  /**
   * Switches to the Suggestions tab with this category pre-filtered.
   * Triggered by clicking a category row or the suggestion count badge.
   */
  onShowSuggestionsForCategory?: (categoryId: string) => void;
}

export function CategoriesTab({
  categories,
  isLoading,
  error,
  onRefresh,
  onShowSuggestionsForCategory,
}: CategoriesTabProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ConditionSuggestionCategory | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleCreate = () => {
    setEditingCategory(null);
    setIsModalOpen(true);
  };

  const handleEdit = (cat: ConditionSuggestionCategory) => {
    setEditingCategory(cat);
    setIsModalOpen(true);
  };

  const handleDeleteRequest = (id: string) => {
    setDeletingId(id);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingId) return;
    setIsDeleting(true);
    try {
      await deleteCategory(deletingId);
      toast.success('Kategorie gelöscht.');
      onRefresh();
    } catch (err: unknown) {
      const apiError = err as { statusCode?: number; message?: string };
      if (apiError.statusCode === 409) {
        toast.error(
          'Kategorie hat noch Vorschläge — erst Vorschläge entfernen oder verschieben.',
        );
      } else {
        toast.error(apiError.message ?? 'Fehler beim Löschen der Kategorie.');
      }
    } finally {
      setIsDeleting(false);
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        <span className="ml-3 text-gray-500">Lade Kategorien...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error.message}</p>
          <Button onClick={onRefresh}>Erneut versuchen</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {categories.length === 0
            ? 'Noch keine Kategorien vorhanden.'
            : `${categories.length} Kategorie${categories.length !== 1 ? 'n' : ''}`}
        </p>
        <Button onClick={handleCreate} size="sm">
          <Plus className="w-4 h-4 mr-2" />
          Neue Kategorie
        </Button>
      </div>

      {/* Table */}
      {categories.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Tag className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">Noch keine Kategorien vorhanden.</p>
            <Button onClick={handleCreate}>
              Erste Kategorie erstellen
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Beschreibung</TableHead>
                <TableHead className="text-right w-32">Vorschläge</TableHead>
                <TableHead className="text-right w-28">Reihenfolge</TableHead>
                <TableHead className="text-right w-24">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.map((cat) => {
                const navigable = !!onShowSuggestionsForCategory;
                const goToSuggestions = () => onShowSuggestionsForCategory?.(cat.id);
                return (
                  <TableRow
                    key={cat.id}
                    className={navigable ? 'cursor-pointer hover:bg-gray-50' : undefined}
                    onClick={navigable ? goToSuggestions : undefined}
                    role={navigable ? 'button' : undefined}
                    tabIndex={navigable ? 0 : undefined}
                    onKeyDown={
                      navigable
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              goToSuggestions();
                            }
                          }
                        : undefined
                    }
                    title={navigable ? `Vorschläge der Kategorie "${cat.name}" anzeigen` : undefined}
                  >
                    <TableCell className="font-medium">{cat.name}</TableCell>
                    <TableCell className="text-gray-500 max-w-xs truncate">
                      {cat.description ?? <span className="italic text-gray-300">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="secondary">{cat.suggestionCount ?? 0}</Badge>
                    </TableCell>
                    <TableCell className="text-right text-gray-500">
                      {cat.displayOrder}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(cat);
                          }}
                          title="Bearbeiten"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteRequest(cat.id);
                          }}
                          className="text-red-500 hover:text-red-700"
                          title="Löschen"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create / Edit Modal */}
      <CategoryFormModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        editingCategory={editingCategory}
        onSaved={onRefresh}
        onCreate={createCategory}
        onUpdate={updateCategory}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deletingId !== null}
        onOpenChange={(open) => { if (!open) setDeletingId(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kategorie löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Aktion kann nicht rückgängig gemacht werden. Kategorien mit
              zugeordneten Vorschlägen können nicht gelöscht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Löschen...
                </>
              ) : (
                'Löschen'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
